<script setup>
import {ref, computed} from 'vue'
import { useCustomerStore } from '@/stores/customer.js'
import {useToast} from "primevue";
import Card from "primevue/card";
import Button from 'primevue/button';
import CustomersTable from "../../components/customer/CustomersTable.vue";
import CustomerForm from '../../components/customer/CustomerForm.vue'
import DefaultLayout from '../../layouts/default.vue'
import InputText from "primevue/inputtext";
import InputIcon from "primevue/inputicon";
import IconField from "primevue/iconfield";
import ActiveArchiveToggle from '@/components/common/ActiveArchiveToggle.vue'
import customerApi from '@/api/customerApi.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { useRoute } from 'vue-router';


const showAddDialog = ref(false)
const customerStore = useCustomerStore();
const toast = useToast(); // Инициализируем
const search = ref('')
const checked = ref(true); // true = активные, false = архив
const activeCount = ref(null);
const archiveCount = ref(null);
const { hasPermission } = usePermissions();
const route = useRoute();
const responsible = computed(() => route.query.responsible ?? undefined);


async function handleSave(clientData) {
  try {
    // отправляем плоский объект как ожидает бекенд
    await customerStore.addCustomer(clientData);
    // закрываем диалог только после успешного ответа
    showAddDialog.value = false;

    toast.add({
      severity: 'success',
      summary: 'Клиент добавлен',
      detail: 'Клиент успешно добавлен',
      life: 2500,
    });

    // при необходимости можно обновить список:
    // await customerStore.loadCustomers({ page: 1, search: search.value, is_active: checked.value });
  } catch (error) {
    // по возможности показываем ошибки бека
    const details = error?.response?.data;
    const firstField = details && typeof details === 'object'
        ? Object.entries(details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ')
        : 'Не удалось добавить клиента';

    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: firstField,
      life: 4000,
    });
  }
}


// Подсчёт активных/архивных в текущей выборке
async function fetchCustomerCounts({ q }) {
  const [activeResp, archiveResp] = await Promise.all([
    customerApi.getCustomers({ page: 1, q, is_active: true, responsible: responsible.value }),
    customerApi.getCustomers({ page: 1, q, is_active: false, responsible: responsible.value }),
  ]);
  return {
    active: Number(activeResp?.count ?? 0),
    archive: Number(archiveResp?.count ?? 0)
  };
}

async function fetchArchiveCountOnly({ q }) {
  const archiveResp = await customerApi.getCustomers({
    page: 1,
    q,
    is_active: false,
    responsible: responsible.value
  });
  return Number(archiveResp?.count ?? 0);
}

function handleInitialCountLoaded({ count, isActive }) {
  if (isActive === true) {
    activeCount.value = count;
  } else if (isActive === false) {
    archiveCount.value = count;
  }
}

</script>
<template>
  <DefaultLayout>
    <Card class="w-full flex-1 flex flex-col min-h-0"
          :pt="{
            body: { class: 'flex-1 flex flex-col h-full' },
            content: { class: 'p-0 flex-1 flex flex-col min-h-0' }
          }"
    >
    <template #title>
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText v-model="search" placeholder="Поиск клиента..."/>
        </IconField>
          <ActiveArchiveToggle
              v-if="hasPermission('customer.archive_customer')"
              v-model="checked"
              :search="search"
              :initial-active-count="activeCount"
              :initial-archive-count="archiveCount"
              :skip-initial-load="true"
              :fetchCounts="fetchCustomerCounts"
              :fetchArchiveCountOnly="fetchArchiveCountOnly"
              onIcon="pi pi-sun"
              offIcon="pi pi-moon"
              widthClass="w-36"
              ariaLabel="Фильтр по активности клиентов"
          />
        </div>

          <Button
              v-if="hasPermission('customer.add_customer')"
              label="Добавить клиента"
              icon="pi pi-plus"
              severity="info"
              rounded
              @click="showAddDialog = true"
          />
        </div>
        <CustomerForm v-model:visible="showAddDialog" @save="handleSave" />

    </template>
    <template #content>
      <CustomersTable
          :search="search"
          :is-active="checked"
          :responsible="responsible"
          @initial-count-loaded="handleInitialCountLoaded"
      />
    </template>
  </Card>
  </DefaultLayout>
</template>

<style scoped>
</style>
